import {Alert, Box, Button, CircularProgress, Drawer, IconButton, MenuItem, Stack, TextField, Typography} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import NotesRoundedIcon from '@mui/icons-material/NotesRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import {formatTicketDate, getTicketDraft, statusOptions, TicketAvatar, TicketPriority, TicketStatus} from './supportTicketUI';

export default function SupportTicketDetails({ticket, draft, saving, error, onChange, onClose, onSave}) {
  const original = ticket ? getTicketDraft(ticket) : {};
  const safeDraft = {
    status: draft?.status || original.status || 'open',
    adminNote: draft?.adminNote || '',
    adminReply: draft?.adminReply || '',
  };
  const dirty = ticket && Object.keys(original).some(key => safeDraft[key] !== (original[key] || ''));
  const hasNewReply = Boolean(safeDraft.adminReply.trim() && safeDraft.adminReply.trim() !== (original.adminReply || '').trim());

  return (
    <Drawer anchor="right" open={Boolean(ticket)} onClose={saving ? undefined : onClose}
      sx={{zIndex: theme => theme.zIndex.drawer + 2}}
      slotProps={{paper: {className: 'support-details', role: 'dialog', 'aria-modal': true, 'aria-labelledby': 'support-detail-title', sx: {width: {xs: '100%', sm: 540}, maxWidth: '100%', boxShadow: '-12px 0 48px #0f172a14'}}}}>
      {ticket && <>
        <div className="support-detail-header">
          <div>
            <span className="support-eyebrow">DESTEK MERKEZİ</span>
            <Typography id="support-detail-title" component="h2" sx={{fontSize: 20, fontWeight: 700, color: '#17243b', mt: 0.5}}>Talep detayı</Typography>
          </div>
          <IconButton aria-label="Detay panelini kapat" onClick={onClose} disabled={saving}><CloseRoundedIcon /></IconButton>
        </div>

        <div className="support-detail-body">
          <Stack direction="row" spacing={1} sx={{mb: 2}}><TicketStatus status={ticket.status} /><TicketPriority priority={ticket.priority} /></Stack>
          <Typography component="h3" sx={{fontSize: 23, fontWeight: 700, letterSpacing: '-0.5px', color: '#17243b', overflowWrap: 'anywhere'}}>{ticket.subject || 'Başlıksız talep'}</Typography>
          <Typography sx={{fontSize: 12, color: '#7c8799', mt: 1}}>{formatTicketDate(ticket.createdAt, true)}</Typography>

          <div className="support-message-card">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <TicketAvatar name={ticket.userName} size={40} />
              <Box sx={{minWidth: 0}}>
                <Typography sx={{fontSize: 14, fontWeight: 650, color: '#26354b', overflowWrap: 'anywhere'}}>{ticket.userName || 'İsimsiz kullanıcı'}</Typography>
                <Typography sx={{fontSize: 12, color: '#718096', overflowWrap: 'anywhere'}}>{ticket.userEmail || 'E-posta belirtilmedi'}</Typography>
              </Box>
            </Stack>
            <Typography sx={{fontSize: 14, lineHeight: 1.85, color: '#475569', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', mt: 2.5}}>{ticket.message || 'Mesaj içeriği bulunmuyor.'}</Typography>
          </div>

          <Box component="form" id="support-ticket-form" onSubmit={event => {event.preventDefault(); if (dirty && !saving) onSave();}}>
            <TextField select label="Durum" value={safeDraft.status} onChange={event => onChange('status', event.target.value)} fullWidth size="small" disabled={saving}>
              {statusOptions.filter(option => option.value).map(option => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </TextField>

            <div className="support-field-heading"><NotesRoundedIcon /><span>EKİP NOTU</span><span className="support-private-label">Yalnızca yöneticiler</span></div>
            <TextField label="Admin notu" placeholder="Ekibiniz için bir not ekleyin…" multiline minRows={3} fullWidth value={safeDraft.adminNote}
              onChange={event => onChange('adminNote', event.target.value)} disabled={saving} slotProps={{htmlInput: {maxLength: 1000}}}
              helperText={`${safeDraft.adminNote.length}/1000 · Kullanıcıya gösterilmez.`} />

            <div className="support-field-heading"><MailOutlineRoundedIcon /><span>KULLANICI YANITI</span></div>
            <TextField label="Kullanıcıya yanıt" placeholder="Kullanıcıya iletmek istediğiniz yanıtı yazın…" multiline minRows={4} fullWidth value={safeDraft.adminReply}
              onChange={event => onChange('adminReply', event.target.value)} disabled={saving} slotProps={{htmlInput: {maxLength: 2000}}}
              helperText={`${safeDraft.adminReply.length}/2000`} />
            <Typography sx={{display: 'flex', gap: 1, fontSize: 12, lineHeight: 1.7, color: '#718096', mt: 1}}>
              <MailOutlineRoundedIcon sx={{fontSize: 16, flexShrink: 0, mt: 0.25}} />
              {ticket.userEmail ? 'Yeni veya değiştirilen yanıt, kaydettiğinizde kullanıcıya e-posta ile gönderilir ve uygulamada görünür.' : 'Yanıt uygulamada görünür. Bu kullanıcı için e-posta adresi bulunmuyor.'}
            </Typography>
            {error && <Alert severity="error" sx={{mt: 2}}>{error}</Alert>}
          </Box>
        </div>

        <div className="support-detail-footer">
          {dirty && <Typography sx={{fontSize: 12, color: '#8b691d', mb: 1.5}}>Kaydedilmemiş değişiklikler · Taslağınız bu oturumda korunur.</Typography>}
          <Stack direction="row" spacing={1.5}>
            <Button variant="outlined" onClick={onClose} disabled={saving} sx={{color: '#64748b', borderColor: '#dbe2eb'}}>Kapat</Button>
            <Button type="submit" form="support-ticket-form" variant="contained" disabled={saving || !dirty} fullWidth startIcon={saving ? <CircularProgress size={16} color="inherit" /> : hasNewReply ? <MailOutlineRoundedIcon /> : <SaveOutlinedIcon />}>
              {saving ? 'Kaydediliyor…' : hasNewReply ? 'Kaydet ve yanıtla' : 'Değişiklikleri kaydet'}
            </Button>
          </Stack>
        </div>
      </>}
    </Drawer>
  );
}
